import { AdAccountDTO, GoogleAdsAdAccountDTO, RefundRequestDTO } from 'api-models'
import { useMemo } from 'react'
import { TERMINAL_STATUSES } from './constants'

/**
 * Which screen the Refunds Agent page renders for the selected ad account (§2–§5).
 *
 * - `no-account`  — the asset has no connected Google Ads account (C2).
 * - `setup`       — a GAds account is selected but the agent has never been
 *                   launched for it (C3).
 * - `empty`       — agent launched but no terminal submissions yet (C4).
 * - `standard`    — agent launched with ≥1 terminal submission (C5).
 *
 * Setup is gated on "never provisioned" — NOT on the toggle or the MCC link.
 * Once launched the agent keeps its standard/empty view even when the toggle is
 * off (§6.2) or MCC access is lost (§6.3): those states are surfaced by the
 * Configuration panel and the access-lost banner, both of which are only
 * reachable from the standard/empty views.
 */
export type RefundsAgentView = 'no-account' | 'setup' | 'empty' | 'standard'

export interface RefundsAgentState {
	view: RefundsAgentView
	/** GAds accounts on this asset (ACTIVE only). */
	gadsAccounts: GoogleAdsAdAccountDTO[]
	/** The selected GAds account, resolved from the breadcrumb. */
	selected: GoogleAdsAdAccountDTO | null
	linked: boolean
	enabled: boolean
}

const isGads = (a: AdAccountDTO): a is GoogleAdsAdAccountDTO =>
	a.platform === 'GADS' && a.status === 'ACTIVE'

/**
 * Has the agent ever been provisioned for this account? `/refunds/launch` sets all
 * three of these together, but the MVP gear panel could set `enabled`/`frequency`
 * without ever scheduling a Next date, so any one of them means setup already
 * happened. `enabled` alone is not sufficient — it goes false whenever the user
 * toggles the agent off or §9 auto-disables it, neither of which un-provisions it.
 */
const isProvisioned = (a: GoogleAdsAdAccountDTO): boolean =>
	a.preferences?.auto_refunds_enabled === true ||
	a.preferences?.auto_refunds_next_submission_at != null ||
	a.preferences?.auto_refunds_frequency != null

export const useRefundsAgentState = (
	adAccounts: AdAccountDTO[],
	selectedAdAccountId: string | null,
	items: RefundRequestDTO[]
): RefundsAgentState => {
	return useMemo(() => {
		const gadsAccounts = adAccounts.filter(isGads)
		const selected =
			gadsAccounts.find((a) => a.id === selectedAdAccountId) ?? null

		if (gadsAccounts.length === 0) {
			return { view: 'no-account', gadsAccounts, selected: null, linked: false, enabled: false }
		}

		// Still resolving the pre-selection — treat as setup shell until known.
		if (!selected) {
			return { view: 'setup', gadsAccounts, selected: null, linked: false, enabled: false }
		}

		const linked = selected.details?.mcc_support_connection === 'LINKED'
		const enabled = selected.preferences?.auto_refunds_enabled === true

		if (!isProvisioned(selected)) {
			return { view: 'setup', gadsAccounts, selected, linked, enabled }
		}

		const hasTerminal = items.some(
			(i) =>
				i.ad_account_id === selected.id &&
				(TERMINAL_STATUSES as readonly string[]).includes(i.status)
		)

		return {
			view: hasTerminal ? 'standard' : 'empty',
			gadsAccounts,
			selected,
			linked,
			enabled
		}
	}, [adAccounts, selectedAdAccountId, items])
}
